/**
 * Context Change Warning Modal
 *
 * Displayed when a user tries to change location or dates while having items in cart.
 * Warns them that changing context will invalidate their cart and require re-validation.
 */

import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { AlertTriangle, ShoppingCart, Calendar, MapPin, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ContextChangeWarningModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    changeType: 'location' | 'dates' | 'both';
    itemCount: number;
    currentLocation?: string;
    newLocation?: string;
    currentDates?: string;
    newDates?: string;
}

export const ContextChangeWarningModal = ({
    isOpen,
    onClose,
    onConfirm,
    changeType,
    itemCount,
    currentLocation,
    newLocation,
    currentDates,
    newDates,
}: ContextChangeWarningModalProps) => {
    const getTitle = () => {
        switch (changeType) {
            case 'location':
                return 'Change pickup location?';
            case 'dates':
                return 'Change rental dates?';
            case 'both':
                return 'Change location and dates?';
        }
    };

    const getDescription = () => {
        const items = itemCount === 1 ? '1 item' : `${itemCount} items`;
        switch (changeType) {
            case 'location':
                return `You have ${items} in your cart for pickup in ${currentLocation}. If you switch to ${newLocation}, we’ll re-check availability there before you continue.`;
            case 'dates':
                return `You have ${items} in your cart. Changing your rental dates means we need to re-check availability and pricing for every item.`;
            case 'both':
                return `You have ${items} in your cart. Changing both location and dates means we need to re-check availability and pricing for everything in your cart.`;
        }
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/50" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                {/* Close Button */}
                                <button
                                    onClick={onClose}
                                    aria-label="Close dialog"
                                    className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground/70 hover:bg-muted hover:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                                    type="button"
                                >
                                    <X className="h-5 w-5" />
                                </button>

                                {/* Icon */}
                                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-amber-100">
                                    <AlertTriangle className="h-6 w-6 text-amber-600" />
                                </div>

                                {/* Title */}
                                <Dialog.Title
                                    as="h3"
                                    className="text-lg font-semibold text-foreground text-center"
                                >
                                    {getTitle()}
                                </Dialog.Title>

                                {/* Description */}
                                <p className="mt-2 text-sm text-muted-foreground text-center">
                                    {getDescription()}
                                </p>

                                {/* Change Details */}
                                <div className="mt-4 space-y-3">
                                    {/* Location Change */}
                                    {(changeType === 'location' || changeType === 'both') && currentLocation && newLocation && (
                                        <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                                            <MapPin className="h-5 w-5 text-muted-foreground/70 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-muted-foreground">Location</p>
                                                <p className="text-sm">
                                                    <span className="text-muted-foreground line-through">{currentLocation}</span>
                                                    <span className="mx-2 text-muted-foreground/70">→</span>
                                                    <span className="font-medium text-foreground">{newLocation}</span>
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Dates Change */}
                                    {(changeType === 'dates' || changeType === 'both') && currentDates && newDates && (
                                        <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                                            <Calendar className="h-5 w-5 text-muted-foreground/70 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-muted-foreground">Dates</p>
                                                <p className="text-sm">
                                                    <span className="text-muted-foreground line-through">{currentDates}</span>
                                                    <span className="mx-2 text-muted-foreground/70">→</span>
                                                    <span className="font-medium text-foreground">{newDates}</span>
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Cart Items */}
                                    <div className="flex items-center gap-3 p-3 bg-warning-subtle rounded-lg">
                                        <ShoppingCart className="h-5 w-5 text-warning flex-shrink-0" />
                                        <div className="flex-1">
                                            <p className="text-sm text-warning">
                                                {itemCount === 1
                                                    ? '1 item in your cart may be affected'
                                                    : `${itemCount} items in your cart may be affected`
                                                }
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Warning Note */}
                                <p className="mt-4 text-xs text-muted-foreground text-center">
                                    If anything is no longer available, we&apos;ll let you review your cart before checkout.
                                </p>

                                {/* Actions */}
                                <div className="mt-6 flex gap-3">
                                    <Button
                                        variant="outline"
                                        onClick={onClose}
                                        className="flex-1"
                                    >
                                        Keep current selection
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            onConfirm();
                                            onClose();
                                        }}
                                        className="flex-1"
                                    >
                                        Continue & Revalidate
                                    </Button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default ContextChangeWarningModal;
